import { useCallback } from "react"
import { useAuthBearerFetch, bearerFetcher, MatsuriError } from "matsuri-hooks"
import { useAuth } from "m2m-components/storage/useM2mAuth"
import { m2mCleaning_cleanerV4 } from "@/endpoints/m2m-cleaning.cleaner-v4"
import { uploadBlobWithPresignedPut } from "@/utils/presignedUpload"

// 忘れ物のカテゴリ。廃棄物は報告しない（API にも値が無い）ため、この2つだけ。
export type LostItemCategory = "valuable" | "general"

// 担当清掃が忘れ物報告の対象エリアか（現状は東京のみ）。
interface LostItemReportTargetResponse {
    isTarget: boolean
    areaName?: string | null
}

export interface LostItemReportImage {
    id: string
    imageUrl: string
    orderNum: number
}

export interface LostItemReport {
    id: string
    cleaningId: string
    category: LostItemCategory
    itemDescription: string
    storagePlace: string
    note?: string | null
    reportedBy: string
    // 報告日時（UNIX 秒）
    reportedAt: number
    // Slack 通知前のみ true。通知後は WORK が回収に動いている可能性があるため削除させない。
    isDeletable: boolean
    images: LostItemReportImage[]
}

interface LostItemReportListResponse {
    reports: LostItemReport[]
}

export interface CreateLostItemReportInput {
    category: LostItemCategory
    itemDescription: string
    storagePlace: string
    note?: string | null
    imageUploadIds: string[]
}

/**
 * 忘れ物報告画面のデータ取得（対象判定・報告済み一覧）と、報告の作成/削除・写真アップロードをまとめたフック。
 *
 * - `isTarget`: 対象エリアか（取得前は undefined）。対象でない清掃はこの画面をスキップする。
 * - `reports`: この清掃で報告済みの忘れ物（reportedAt 昇順）。1件ずつ即永続化するため、
 *   画面をリロードしても送信済みの分はここから復元できる。
 * - `error`: 対象判定の失敗のみ。一覧取得の失敗は degrade させ、報告フロー自体は継続できるようにする
 *   （ゴミストッカー確認と同じ方針）。
 * - `uploadImage(blob, signal)`: 1枚分の issue + S3 PUT を行い uploadId を返す（useImmediatePhotoUpload に注入）
 * - `createReport(input)` / `deleteReport(reportId)`: 成功後に一覧を再取得する
 */
export const useLostItemReport = (cleaningId: string) => {
    const { token } = useAuth()

    // まず対象判定。対象でなければ一覧の取得は行わない。
    const {
        data: target,
        isLoading: isTargetLoading,
        error: targetError
    } = useAuthBearerFetch<LostItemReportTargetResponse>(
        token,
        cleaningId
            ? m2mCleaning_cleanerV4.getLostItemReportTarget({ cleaningId })
            : null,
        {
            method: m2mCleaning_cleanerV4.getLostItemReportTarget.method
        }
    )

    const isTarget = target?.isTarget

    const {
        data: list,
        isLoading: isListLoading,
        refetch
    } = useAuthBearerFetch<LostItemReportListResponse>(
        token,
        cleaningId && isTarget
            ? m2mCleaning_cleanerV4.getLostItemReports({ cleaningId })
            : null,
        {
            method: m2mCleaning_cleanerV4.getLostItemReports.method
        }
    )

    const uploadImage = useCallback(
        async (blob: Blob, signal: AbortSignal) =>
            uploadBlobWithPresignedPut({
                token,
                issueUrl:
                    m2mCleaning_cleanerV4.issueLostItemReportImageUploadUrl({
                        cleaningId
                    }),
                blob,
                signal
            }),
        [token, cleaningId]
    )

    const createReport = useCallback(
        async (input: CreateLostItemReportInput) => {
            const { data: report, error } = await bearerFetcher<LostItemReport>(
                token ?? "",
                m2mCleaning_cleanerV4.createLostItemReport({ cleaningId }),
                {
                    method: "POST",
                    body: JSON.stringify(input)
                }
            )
            if (error) {
                throw error
            }
            if (!report) {
                throw new Error("empty response")
            }
            await refetch()
            return report
        },
        [token, cleaningId, refetch]
    )

    const deleteReport = useCallback(
        async (reportId: string) => {
            const { error } = await bearerFetcher(
                token ?? "",
                m2mCleaning_cleanerV4.deleteLostItemReport({
                    cleaningId,
                    reportId
                }),
                {
                    method: "DELETE"
                }
            )
            if (error) {
                throw error
            }
            await refetch()
        },
        [token, cleaningId, refetch]
    )

    // 対象判定が解決するまで（と対象時は一覧の取得中も）は読込中扱い。
    const isLoading = isTargetLoading || (isTarget === true && isListLoading)

    // ブロックするのは対象判定の失敗のみ（対象か分からないまま進めると誤報告になるため）。
    const error: MatsuriError | undefined = targetError

    return {
        isTarget,
        areaName: target?.areaName ?? undefined,
        reports: list?.reports ?? [],
        isLoading,
        error,
        uploadImage,
        createReport,
        deleteReport
    }
}
